/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * CreateEditRoleComponent
 * Form for creating an application user role.
 * - Name: internal key (no spaces, alphanumeric + underscore), required, max 100
 * - Label: display name (alphanumeric + spaces), required, max 100
 * - Validates inputs per requirements
 * - Centered compact action buttons
 */
import React, { useState, useEffect } from 'react';
import { useTheme } from '../../theme/ThemeContext'; // Theme context for colors and radius
import Button from '../Button';
import ThemedInput from '../common/ThemedInput';
import ThemedTextArea from '../common/ThemedTextArea';
import { useParams } from 'react-router-dom'; // To get applicationId from route

// Role permissions options
const PERMISSIONS = [
    { value: 'read', label: 'Read' },
    { value: 'create', label: 'Create' },
    { value: 'update', label: 'Update' },
    { value: 'delete', label: 'Delete' }
];

// Validation regex:
// - NAME (key): letters, numbers, underscore; NO spaces
// - LABEL (display): letters, numbers, spaces; NO other special chars
const NAME_REGEX = /^[a-zA-Z0-9_]+$/;
const LABEL_REGEX = /^[a-zA-Z0-9 ]+$/;

function CreateEditRoleComponent({ onSubmit, onCancel, serverErrors = {}, initialData = null, isEdit = false }) {
    const { theme } = useTheme();
    const { applicationId } = useParams();

    // Controlled form state for fields and errors
    const [form, setForm] = useState({
        name: '',               // internal key
        label: '',              // display name
        description: '',
        default_permissions: []
    });
    const [errors, setErrors] = useState({});

    // Map serverErrors (if any) into local error state
    useEffect(() => {
        if (!onSubmit) return;
        if (typeof serverErrors !== 'object' || serverErrors === null) return;

        const mapped = {};
        for (const [k, v] of Object.entries(serverErrors)) {
            if (Array.isArray(v)) mapped[k] = v.join(' ');
            else mapped[k] = String(v);
        }
        setErrors(prev => ({ ...prev, ...mapped }));

        const timers = [];
        Object.keys(mapped).forEach(field => {
            const t = setTimeout(() => {
                setErrors(prev => ({ ...prev, [field]: '' }));
            }, 4000);
            timers.push(t);
        });

        return () => timers.forEach(t => clearTimeout(t));
    }, [serverErrors, onSubmit]);

    function handleChange(e) {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    }

    function handlePermissionChange(e) {
        const { value, checked } = e.target;
        setForm(prev => {
            const updated = checked
                ? [...prev.default_permissions, value]
                : prev.default_permissions.filter(p => p !== value);
            return { ...prev, default_permissions: updated };
        });
    }

    useEffect(() => {
        if (isEdit && initialData) {
            setForm({
                name: initialData.name || "",
                label: initialData.label || "",
                description: initialData.description || "",
                default_permissions: initialData.default_permissions || []
            });
        }
    }, [isEdit, initialData]);

    /**
     * Validates the fields per requirements and returns error object
     */
    function validate(values) {
        let errors = {};
        const trimmedName = values.name.trim();
        const trimmedLabel = values.label.trim();

        // Name (key): required, alphanumeric + underscore, NO spaces, max 100
        if (!trimmedName) {
            errors.name = 'Role key (Name) is required';
        } else if (trimmedName.length > 100) {
            errors.name = 'Name cannot exceed 100 characters';
        } else if (!NAME_REGEX.test(trimmedName)) {
            errors.name = 'Name can contain only letters, numbers, and underscore (_), no spaces';
        }

        // Label (display): required, alphanumeric + spaces, max 100
        if (!trimmedLabel) {
            errors.label = 'Label (display name) is required';
        } else if (trimmedLabel.length > 100) {
            errors.label = 'Label cannot exceed 100 characters';
        } else if (!LABEL_REGEX.test(trimmedLabel)) {
            errors.label = 'Label can contain only letters, numbers, and spaces';
        }

        // Description: length check
        if (values.description && values.description.trim().length > 256) {
            errors.description = 'Description cannot exceed 256 characters';
        }

        // Permissions: must select at least 1
        if (!values.default_permissions.length) {
            errors.default_permissions = 'Select at least one permission';
        }

        return errors;
    }

    /**
     * On form submit: trims input, runs validation, then calls onSubmit if valid
     */
    function handleSubmit(e) {
        e.preventDefault();
        const submitData = {
            ...form,
            name: form.name.trim(),               // key
            label: form.label.trim(),             // display name
            description: form.description.trim() ? form.description.trim() : null,
            application: applicationId
        };
        const vErrors = validate(submitData);
        setErrors(vErrors);
        if (Object.keys(vErrors).length === 0) {
            if (onSubmit) onSubmit(submitData);
        }
    }

    /**
     * On cancel: calls onCancel prop if provided, otherwise resets form
     */
    function handleCancel(e) {
        e.preventDefault();
        if (onCancel) return onCancel();
        setForm({
            name: '',
            label: '',
            description: '',
            default_permissions: []
        });
        setErrors({});
    }

    // Card layout: themed background, radius, and box-shadow, fixed width
    const cardStyle = {
        width: 450,
        maxWidth: '98vw',
        background: '#fff',
        borderRadius: theme.cardRadius,
        boxShadow: '0 6px 32px 0 rgba(80,80,180,0.10)',
        padding: '2.5em 2em 2em 2em',
        margin: '32px auto',
        position: 'relative'
    };

    return (
        <div
            className="d-flex flex-column align-items-center justify-content-center"
            style={{ minHeight: '80vh', background: '#f6f8fa' }}
        >
            <div style={cardStyle}>
                <h3 className="fw-bold mb-4" style={{ color: theme.primary }}>
                    {isEdit ? "Update Role" : "Create Role"}
                </h3>
                <form onSubmit={handleSubmit} autoComplete="off">
                    {/* Name Field (internal key) */}
                    <div className="mb-3">
                        <label
                            htmlFor="name"
                            className="form-label fw-semibold"
                            style={{ color: theme.primary, fontSize: 15 }}
                        >
                            Name (Key)
                        </label>
                        <ThemedInput
                            type="text"
                            id="name"
                            name="name"
                            maxLength={100}
                            value={form.name}
                            onChange={handleChange}
                            error={!!errors.name}
                            placeholder="e.g. ADMIN, VIEWER_1"
                            autoComplete="off"
                        />
                        {/* Hint for name (key) */}
                        <div style={{ fontSize: '0.9em', color: '#888' }}>
                            Internal key for this role. Only letters, numbers, and underscore (_) are allowed.
                            No spaces (e.g. ADMIN, VIEWER_1).
                        </div>
                        {/* Show error if name invalid */}
                        {errors.name && (
                            <div style={{ color: '#c80b23', fontSize: 13, marginTop: 4 }}>
                                {errors.name}
                            </div>
                        )}
                    </div>

                    {/* Label Field (display name) */}
                    <div className="mb-3">
                        <label
                            htmlFor="label"
                            className="form-label fw-semibold"
                            style={{ color: theme.primary, fontSize: 15 }}
                        >
                            Label (Display name)
                        </label>
                        <ThemedInput
                            type="text"
                            id="label"
                            name="label"
                            maxLength={100}
                            value={form.label}
                            onChange={handleChange}
                            error={!!errors.label}
                            placeholder="e.g. Admin, Viewer, Staff User"
                            autoComplete="off"
                        />
                        {/* Hint for label (display name) */}
                        <div style={{ fontSize: '0.9em', color: '#888' }}>
                            Display name shown in the UI. Only letters, numbers, and spaces are allowed
                            (e.g. Admin, Viewer 1).
                        </div>
                        {/* Show error if label invalid */}
                        {errors.label && (
                            <div style={{ color: '#c80b23', fontSize: 13, marginTop: 4 }}>
                                {errors.label}
                            </div>
                        )}
                    </div>

                    {/* Description Field */}
                    <div className="mb-3">
                        <label
                            htmlFor="description"
                            className="form-label fw-semibold"
                            style={{ color: theme.primary, fontSize: 15 }}
                        >
                            Description
                        </label>
                        <ThemedTextArea
                            id="description"
                            name="description"
                            value={form.description}
                            onChange={handleChange}
                            rows={3}
                            maxLength={256}
                            error={!!errors.description}
                            placeholder="Describe the role and its responsibilities"
                        />
                        {/* Hint for description */}
                        <div style={{ fontSize: '0.9em', color: '#888' }}>
                            Short description, e.g. “Can manage users and settings” (max 256 characters).
                        </div>
                        {/* Show error if description invalid */}
                        {errors.description && (
                            <div style={{ color: '#c80b23', fontSize: 13, marginTop: 4 }}>
                                {errors.description}
                            </div>
                        )}
                        <small className="text-muted">
                            {form.description.trim().length} / 256
                        </small>
                    </div>

                    {/* Permissions */}
                    <div className="mb-4">
                        <label
                            className="form-label fw-semibold"
                            style={{ color: theme.primary, fontSize: 15 }}
                        >
                            Permissions
                        </label>
                        <br />
                        <div className="d-flex flex-wrap">
                            {PERMISSIONS.map(perm => (
                                <label key={perm.value} className="me-4 fw-normal">
                                    <input
                                        type="checkbox"
                                        value={perm.value}
                                        checked={form.default_permissions.includes(perm.value)}
                                        onChange={handlePermissionChange}
                                        style={{ marginRight: 5 }}
                                    />
                                    {perm.label}
                                </label>
                            ))}
                        </div>
                        <hr />
                        <p>
                            <small>
                                <b>Important:</b> Go to application users list to assign roles
                                to users or assign table level permissions to each user. Please
                                note that table level permissions shall override role level
                                privileges.
                            </small>
                        </p>
                        {/* Error if no permission selected */}
                        {errors.default_permissions && (
                            <div style={{ color: '#c80b23', fontSize: 13, marginTop: 4 }}>
                                {errors.default_permissions}
                            </div>
                        )}
                    </div>

                    {/* Centered, compact action buttons */}
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '14px',
                            marginTop: '2.2rem'
                        }}
                    >
                        <Button mode="outlined" type="button" onClick={handleCancel}>
                            Cancel
                        </Button>
                        <Button mode="primary-bold" type="submit">
                            {isEdit ? "Update" : "Create"}
                        </Button>
                    </div>
                </form>

                {/* Accent: bottom gradient bar matching theme */}
                <div
                    style={{
                        height: 8,
                        width: '100%',
                        borderBottomLeftRadius: theme.cardRadius,
                        borderBottomRightRadius: theme.cardRadius,
                        background: theme.gradientBackground,
                        position: 'absolute',
                        left: 0,
                        bottom: 0
                    }}
                />
            </div>
        </div>
    );
}

export default CreateEditRoleComponent;
